import type React from 'react';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

// ─── FetchState ───────────────────────────────────────────────────────────────

export type FetchState = 'idle' | 'loading' | 'success' | 'error';

// ─── AgentIntegrationInfoDialog ───────────────────────────────────────────────

const REQUEST_EXAMPLE = `POST <agent-base-url><extension-path>
Content-Type: application/json

{
  "context": {
    "user": {
      "identifier": "user-abc-123",
      "firstName": "Jane",
      "lastName": "Smith",
      "email": "jane.smith@example.com",
      "roles": ["user"],
      "permissions": ["dashboard:view"],
      "group": {
        "identifier": "group-xyz",
        "name": "Acme Corp"
      }
    },
    "brand": {
      "identifier": "brand-456",
      "name": "Acme Brand",
      "access": "1"
    }
  }
}`;

const RESPONSE_EXAMPLE_CHART = `// Bar chart example
{
  "type": "chart",
  "data": {
    "type": "bar",
    "chart": {
      "title": "Monthly Revenue",
      "xAxisTitle": "Month",
      "yAxisTitle": "Revenue ($K)",
      "indexBy": "x",
      "data": [
        { "x": "Jan", "y": 142 },
        { "x": "Feb", "y": 165 }
      ]
    },
    "colors": ["#4caf50", "#2196f3"],
    "options": { "unit": "K", "isPrefix": false },
    "insights": "Revenue grew 16% from Jan to Feb."
  }
}`;

const COLORS_INFO = `Colors are optional for all chart types. When provided,
they map to data points/series in order and cycle if
there are fewer colors than data points.

Priority: builder config colors > agent colors > defaults

Supported on: bar, stacked-bar, pie,
  multiple-clustered-bar, multiple-stacked-bar,
  progress-bar-widget, semi-circle-progress-widget`;

const RESPONSE_EXAMPLE_CODE = `// Code widget example — return any JSON shape
{
  "type": "code",
  "data": {
    "title": "My Dashboard",
    "items": [
      { "label": "Revenue", "value": "$2.4M" },
      { "label": "Users", "value": 1420 }
    ]
  }
}`;

const INFO_CODE_STYLE = {
  fontSize: '0.7rem',
  fontFamily: 'monospace',
  m: 0,
  p: 1.5,
  overflow: 'auto',
  maxHeight: 220,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
  backgroundColor: 'grey.100',
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
} as const;

interface AgentIntegrationInfoDialogProps {
  open: boolean;
  onClose: () => void;
  /** When true, includes the code widget data model section */
  showDataModelInfo?: boolean;
}

export function AgentIntegrationInfoDialog({
  open,
  onClose,
  showDataModelInfo,
}: AgentIntegrationInfoDialogProps): React.ReactElement {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Agent Integration Guide
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Section 1: What your agent receives */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            What your agent receives
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            When the dashboard fetches widget data, it sends a <strong>POST</strong> request to your
            agent&apos;s endpoint with the user and brand context in the request body. Your agent can use this
            to conditionally return different data per user or brand.
          </Typography>
          <Box component="pre" sx={INFO_CODE_STYLE}>
            {REQUEST_EXAMPLE}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            The <code>brand</code> field is only present when the user has an active brand selected. The{' '}
            <code>user</code> fields match the same context sent to agents during chat queries.
          </Typography>
        </Box>

        {/* Section 2 (code widget only): Defining a data model */}
        {showDataModelInfo && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Defining a data model
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              The <code>/data.js</code> tab in the editor defines the data shape your component expects. In{' '}
              <strong>Agent</strong> mode, this serves as a contract — edit it to match the JSON your agent
              returns, then reference it in <code>/App.js</code>:
            </Typography>
            <Box component="pre" sx={INFO_CODE_STYLE}>
              {`// /data.js — define expected shape
export const data = {
  "title": "My Dashboard",
  "items": [
    { "label": "Revenue", "value": "$2.4M" }
  ]
};

// /App.js — use your component
import { data } from "./data";

export default function App() {
  return (
    <div>
      <h2>{data.title}</h2>
      {data.items.map((item, i) => (
        <p key={i}>{item.label}: {item.value}</p>
      ))}
    </div>
  );
}`}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              At runtime, the platform fetches from your agent and overwrites <code>/data.js</code> with the
              live response. Your component code stays the same.
            </Typography>
          </Box>
        )}

        {/* Section 3: Passing colors from agent */}
        {!showDataModelInfo && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Passing colors from your agent
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Your agent can include an optional <code>colors</code> array of hex strings in the chart data.
              If a user sets colors in the builder, those take priority over agent-provided colors.
            </Typography>
            <Box component="pre" sx={INFO_CODE_STYLE}>
              {COLORS_INFO}
            </Box>
          </Box>
        )}

        {/* Section 4: What your agent should return */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            What your agent should return
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {showDataModelInfo ? (
              <>
                For code widgets, wrap your custom JSON in{' '}
                <code>{`{ "type": "code", "data": { ... } }`}</code>. The inner <code>data</code> object can
                be any shape — it becomes the content of <code>/data.js</code>.
              </>
            ) : (
              <>
                Your agent must return JSON matching the expected widget data format. Use{' '}
                <strong>Fetch Preview</strong> to test your endpoint. The &quot;Expected response format&quot;
                accordion below shows the exact shape required for the selected widget type.
              </>
            )}
          </Typography>
          <Box component="pre" sx={INFO_CODE_STYLE}>
            {showDataModelInfo ? RESPONSE_EXAMPLE_CODE : RESPONSE_EXAMPLE_CHART}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
